import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Copia um texto para a área de transferência com fallback para navegadores
 * que não expõem a Clipboard API, e expõe um estado `copied` temporário
 * (útil para feedback visual do tipo "Copiado!").
 *
 * @param resetDelay tempo em ms até `copied` voltar para `false` (padrão 1300)
 */
export function useCopyToClipboard(resetDelay = 1300) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), resetDelay);
    },
    [resetDelay]
  );

  return { copied, copy };
}

export default useCopyToClipboard;
